import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { Radio, Plus, Users, Clock, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { PressableCard } from './ui/PressableCard';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { RoomService, lifecycleStateOf } from '../services/RoomService';
import {
    ROOM_MAX_MEMBERS,
    ROOM_MIN_MEMBERS,
    formatTimeRemaining,
    isFull,
} from '../services/roomLifecycle';
import { EVENTS } from '../constants/navigation';
import type { Room } from '../types';
import { CreateRoomModal } from './CreateRoomModal';

/**
 * The public feed of open rooms — the first thing under the Chat tab.
 *
 * Public in the sense that anyone signed in can browse it and see what is
 * running; it is not reachable logged out, and the contents of a room stay
 * members-only. Vertical list, one row per room, the same PressableCard rows the
 * chat list uses.
 */
export function RoomsFeed() {
    const { userProfile, firebaseUser } = useUserData();
    const { isBlockedId } = useContentFilter();
    const [rooms, setRooms] = useState<Room[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    // Ticks once a minute purely to keep every countdown honest without
    // re-reading Firestore.
    const [now, setNow] = useState(() => Date.now());

    const myId = String(firebaseUser?.uid || userProfile?.id || '');

    useEffect(() => {
        if (!myId) return;
        const unsub = RoomService.subscribeToOpenRooms(myId, (next) => {
            setRooms(next);
            setLoading(false);
        }, () => setLoading(false));
        return unsub;
    }, [myId]);

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, []);

    // A room holding anyone the viewer has blocked, in either direction, has no
    // business in their feed: they cannot join it. The server refuses the
    // membership either way (see onRoomJoinRequestApproved), so listing it would
    // only offer a door that does not open — and saying why it does not open
    // would tell them where a blocked person is.
    //
    // The host is covered by the same pass: they are a member like any other.
    const visibleRooms = useMemo(
        () => rooms.filter(r =>
            !isBlockedId(r.ownerId) && !(r.members || []).some(id => isBlockedId(id)),
        ),
        [rooms, isBlockedId],
    );

    const myOpenRoom = useMemo(
        () => visibleRooms.find(r => String(r.ownerId) === myId),
        [visibleRooms, myId],
    );

    const openRoom = (room: Room) => {
        window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_ROOM, { detail: { id: room.id, room } }));
    };

    // The guide opens and closes this modal itself.
    //
    // It sets showCreate directly rather than going through handleCreateClick,
    // which refuses when the host already has a room open. That check is right
    // for a real tap and wrong here: the tour is showing what the form looks
    // like, not starting anything, and nothing in it can be submitted because
    // the overlay swallows every tap.
    useEffect(() => {
        const open = () => setShowCreate(true);
        const close = () => setShowCreate(false);
        window.addEventListener('guide:openCreateRoom', open);
        window.addEventListener('guide:closeCreateRoom', close);
        return () => {
            window.removeEventListener('guide:openCreateRoom', open);
            window.removeEventListener('guide:closeCreateRoom', close);
        };
    }, []);

    const handleCreateClick = () => {
        if (myOpenRoom) {
            toast.error('You already have a room open', {
                description: 'End it before starting another.',
            });
            return;
        }
        setShowCreate(true);
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            {/* Host action */}
            <div className="px-4 pt-3" style={{ paddingBottom: 4 }} data-tour="guide-create-room">
                <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={handleCreateClick}
                    className="w-full py-3 rounded-2xl bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-lg flex items-center justify-center gap-2"
                    style={{ fontWeight: 700 }}
                >
                    <Plus className="w-4 h-4" />
                    {myOpenRoom ? 'Open your room' : 'Start a room'}
                </motion.button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pb-24" data-tour="guide-rooms-list">
                {loading ? (
                    <div className="flex flex-col items-center justify-center" style={{ paddingTop: 64, paddingBottom: 64 }}>
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        >
                            <Radio className="w-8 h-8 text-[#F39C12] dark:text-[#4478ea]" />
                        </motion.div>
                        <p className="text-xs text-[#666666] dark:text-[#a0a0a0] mt-2" style={{ fontWeight: 700 }}>
                            Loading rooms...
                        </p>
                    </div>
                ) : visibleRooms.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-center" style={{ paddingTop: 56, paddingBottom: 56, paddingLeft: 32, paddingRight: 32 }}>
                        <div className="w-24 h-24 rounded-full bg-linear-to-br from-[#F39C12]/20 to-[#E74C3C]/20 dark:from-[#4478ea]/20 dark:to-[#2d1ef1]/20 flex items-center justify-center mb-6">
                            <Radio className="w-12 h-12 text-[#F39C12] dark:text-[#4478ea]" />
                        </div>
                        <h2 className="text-2xl text-black dark:text-white mb-3">No Rooms Open</h2>
                        <p className="text-[#666666] dark:text-[#a0a0a0] max-w-sm">
                            Rooms are live conversations that run for a set time, then disappear.
                            Start one and it shows up here.
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-black/10 dark:divide-white/10">
                        {visibleRooms.map((room) => {
                            const state = lifecycleStateOf(room);
                            const full = isFull(state.memberCount);
                            const isMember = Array.isArray(room.members) && room.members.includes(myId);
                            const isHost = String(room.ownerId) === myId;
                            const waiting = room.status === 'lobby';

                            return (
                                <PressableCard
                                    key={room.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    whileHover={{ x: 5 }}
                                    onClick={() => openRoom(room)}
                                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                >
                                    <div
                                        className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                                        style={{
                                            background: room.status === 'grace'
                                                ? 'rgba(231,76,60,0.15)'
                                                : 'rgba(68,120,234,0.15)',
                                        }}
                                    >
                                        <Radio
                                            className="w-5 h-5"
                                            style={{ color: room.status === 'grace' ? '#E74C3C' : '#4478ea' }}
                                        />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p
                                                className="text-black dark:text-white truncate"
                                                style={{ fontWeight: 700 }}
                                            >
                                                {room.title}
                                            </p>
                                            {full && (
                                                <span
                                                    className="shrink-0 px-2 py-0.5 rounded-full"
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        background: 'rgba(231,76,60,0.15)',
                                                        color: '#E74C3C',
                                                    }}
                                                >
                                                    Full
                                                </span>
                                            )}
                                            {isHost && (
                                                <span
                                                    className="shrink-0 px-2 py-0.5 rounded-full"
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        background: 'rgba(68,120,234,0.18)',
                                                        color: '#4478ea',
                                                    }}
                                                >
                                                    Hosting
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-3 mt-1 text-xs text-[#666666] dark:text-[#a0a0a0]">
                                            <span className="flex items-center gap-1">
                                                <Users className="w-3 h-3" />
                                                {state.memberCount}/{ROOM_MAX_MEMBERS}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {waiting
                                                    ? `Waiting for ${Math.max(0, ROOM_MIN_MEMBERS - state.memberCount)} more`
                                                    : formatTimeRemaining(state.expiresAt, now)}
                                            </span>
                                            {!isMember && (
                                                <span className="flex items-center gap-1">
                                                    <Lock className="w-3 h-3" />
                                                    Request
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </PressableCard>
                            );
                        })}
                    </div>
                )}
            </div>

            <CreateRoomModal
                isOpen={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={(roomId) => {
                    setShowCreate(false);
                    window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_ROOM, { detail: { id: roomId } }));
                }}
            />
        </div>
    );
}

export default RoomsFeed;
